import { useCallback, useEffect, useRef } from "react";
import { nudgeInFlightUploads } from "@/Service/ChunkedUploadService";
import { shouldTriggerRecoveryNudge } from "./shouldTriggerRecoveryNudge";

/**
 * Kicks in-flight tus.Uploads out of their exponential-retry sleep as soon
 * as the network recovers (bug 2026-08-05).
 *
 * Prior to this watcher, a user switching from poor mobile data to fast
 * WiFi could wait 1–2 minutes before bytes started flowing again —
 * tus was asleep in a setTimeout(240s) retry backoff and had no way to
 * know the network had come back. This watcher listens for the browser
 * signals (online / connection.change), decides whether the transition
 * is a genuine improvement (see shouldTriggerRecoveryNudge), and calls
 * `nudgeInFlightUploads` which does `tus.abort(false) + tus.start()` on
 * every non-paused session — cancelling the pending retry and firing an
 * immediate fresh PATCH.
 *
 * Mounts alongside CrossDeviceUploadsWatcher / AutoResumeWatcher in App.js.
 * Renders nothing. Cool-down is enforced service-side to keep this
 * component's logic simple.
 *
 * Distinct from AutoResumeWatcher — that one handles PAUSED uploads
 * (auto-paused via transient error) by dispatching resumeUpload. This
 * one handles ACTIVE uploads stuck in tus's own retry loop.
 */
export const NetworkRecoveryWatcher = () => {
  const lastSnapshotRef = useRef(null);

  const readSnapshot = useCallback(() => {
    if (typeof navigator === "undefined") {
      return { online: true, effectiveType: undefined };
    }
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection ||
      null;
    return {
      online: navigator.onLine !== false,
      effectiveType: connection?.effectiveType,
    };
  }, []);

  const handleChange = useCallback(() => {
    const next = readSnapshot();
    if (shouldTriggerRecoveryNudge(lastSnapshotRef.current, next)) {
      // eslint-disable-next-line no-console
      console.info("[UPLOAD-DIAG] network-recovery/nudge-triggered", {
        prev: lastSnapshotRef.current,
        next,
      });
      try {
        nudgeInFlightUploads();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[UPLOAD-DIAG] network-recovery/nudge-error", {
          message: err?.message,
        });
      }
    }
    lastSnapshotRef.current = next;
  }, [readSnapshot]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // Seed the baseline so the first real event has something to compare
    // against — otherwise the shouldTrigger helper's "first observation"
    // guard would swallow the initial recovery.
    lastSnapshotRef.current = readSnapshot();

    window.addEventListener("online", handleChange);
    window.addEventListener("offline", handleChange);

    const connection =
      (typeof navigator !== "undefined" &&
        (navigator.connection ||
          navigator.mozConnection ||
          navigator.webkitConnection)) ||
      null;
    if (connection && typeof connection.addEventListener === "function") {
      connection.addEventListener("change", handleChange);
    }

    return () => {
      window.removeEventListener("online", handleChange);
      window.removeEventListener("offline", handleChange);
      if (connection && typeof connection.removeEventListener === "function") {
        connection.removeEventListener("change", handleChange);
      }
    };
  }, [handleChange, readSnapshot]);

  return null;
};

export default NetworkRecoveryWatcher;
